import {Injectable} from '@angular/core';
import {SessionService} from './session.service';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngrx/store';
import {AppState} from '../../app/app.state';
import {Device, DeviceId, DeviceIdMap, DevicePatch} from '../../shared/models/device';
import {
  DeviceCreatedAction,
  DeviceDeletedAction,
  DeviceFetchedAction,
  DevicesUserFetchedAction,
  DeviceUpdatedAction,
} from '../actions/devices.actions';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {ModbusPointID, ModbusPoint} from '../../shared/models/modbus';
import {LoRaWANDevicePoint} from '../../shared/models/lorawan';
import {PointValue} from '../../shared/models/point';
import {API_BASE_URL, TUNNEL_BASE_URL} from './env.service';
import {take, map, switchMap} from 'rxjs/operators';
import {PermissionsService} from './permissions.service';
import {Permission} from '../../shared/models/fga/permission';

@Injectable({
  providedIn: 'root',
})
export class DevicesService {
  private fetchedUserDevicesOnce: boolean = false;

  sessUserDevices: DeviceIdMap = {};
  sessUserDevices$: BehaviorSubject<DeviceIdMap> = new BehaviorSubject<DeviceIdMap>({});

  /**
   * If a fetch is currently in progress, this will not be empty.
   * @private
   */
  private fetchUserAppsResults$: Subject<Device[]>;


  constructor(
    private session: SessionService,
    private http: HttpClient,
    private store: Store<AppState>,
    private permissionsService: PermissionsService,
  ) {
    this.store.select('devices').subscribe(
      (state) => {
        for (let device of state.devices) {
          this.sessUserDevices[device.ID] = device;
        }

        this.sessUserDevices$.next(this.sessUserDevices);
      },
    );
  }


  canManageDevice$(deviceId: DeviceId): Observable<boolean> {
    return this.getUserDevices$().pipe(
      take(1),
      map(devices => devices.find(d => d.ID === deviceId)),
      switchMap(device => {
        if (!device) {
          return of(false);
        }

        if (device.Owner === this.session.userId) {
          return of(true);
        }

        return this.permissionsService.hasPermissions({
          UserID: this.session.userId,
          Permissions: [Permission.CAN_CONFIGURE]
        });
      })
    );
  }


  /**
   * Returns an observable that emits the user's devices. More critically,
   * if user devices have already been fetched from server, returns an
   * observable that emits that cached array; otherwise triggers a fetch
   * from the API and returns observable that emits the response from
   * the server.
   */
  getUserDevices$(): Observable<Device[]> {
    if (this.fetchedUserDevicesOnce) {
      return of(Object.values(this.sessUserDevices));
    }

    if (this.fetchUserAppsResults$) {
      return this.fetchUserAppsResults$;
    }

    this.fetchUserAppsResults$ = new Subject<Device[]>();

    this.fetchUserDevices$().subscribe({
      next: (devices) => {
        this.fetchUserAppsResults$.next(devices);
        this.fetchUserAppsResults$.complete();
        delete this.fetchUserAppsResults$;
      },
    });

    return this.fetchUserAppsResults$;
  }


  /**
   * Call this to get user devices from the API directly with no cache.
   */
  fetchUserDevices$(): Observable<Device[]> {
    return new Observable<Device[]>(
      subscriber => {
        this.http.get<Device[]>(`${API_BASE_URL}/devices/user/${this.session.userId}`).subscribe(
          (devices): void => {
            this.store.dispatch(new DevicesUserFetchedAction(devices));

            subscriber.next(devices);
            subscriber.complete();

            this.fetchedUserDevicesOnce = true;
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  /**
   *
   * @param device
   */
  createDevice$(device: Device): Observable<Device> {
    return new Observable<Device>(
      subscriber => {
        this.http.post<Device>(`${API_BASE_URL}/devices`, device).subscribe(
          (data) => {
            device.ID = data.ID;
            this.store.dispatch(new DeviceCreatedAction(device));

            subscriber.next(data);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  /**
   *
   * @param deviceId
   */
  fetchDevice$(deviceId: DeviceId): Observable<Device> {
    return new Observable<Device>(
      subscriber => {
        this.http.get<Device>(`${API_BASE_URL}/devices/${deviceId}`).subscribe(
          (device) => {
            this.store.dispatch(new DeviceFetchedAction(device));

            subscriber.next(device);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  /**
   *
   * @param deviceId
   * @param patch
   */
  patchDevice$(deviceId: DeviceId, patch: DevicePatch): Observable<Device> {
    return new Observable<Device>(
      subscriber => {
        this.http.patch<Device>(`${API_BASE_URL}/devices/${deviceId}`, patch).subscribe(
          (device) => {
            this.store.dispatch(new DeviceUpdatedAction(device));

            subscriber.next(device);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  /**
   *
   * @param deviceId
   */
  deleteDevice$(deviceId: DeviceId): Observable<null> {
    return new Observable<null>((subscriber) => {
      this.http.delete<null>(`${API_BASE_URL}/devices/${deviceId}`).subscribe(
        () => {
          this.store.dispatch(new DeviceDeletedAction(deviceId));
          delete this.sessUserDevices[deviceId];

          subscriber.next(null);
          subscriber.complete();
        },
        () => {
          subscriber.complete();
        },
      );
    });
  }


  resetTimeout$(deviceId: DeviceId): Observable<null> {
    return this.http.delete<null>(`${TUNNEL_BASE_URL}/remote/devices/${deviceId}/timeout`);
  }


  saveModbusPoint$(point: ModbusPoint, deviceId: DeviceId): Observable<null> {
    return this.http.put<null>(`${API_BASE_URL}/devices/${deviceId}/config/modbus/points/${point.ID}`, point);
  }


  deleteModbusPoint$(pointId: ModbusPointID, deviceId: DeviceId): Observable<null> {
    return this.http.delete<null>(`${API_BASE_URL}/devices/${deviceId}/config/modbus/points/${pointId.serialize()}`);
  }


  saveLoRaWANPoint$(point: LoRaWANDevicePoint, deviceId: DeviceId, oldPoint: LoRaWANDevicePoint | null): Observable<Device> {
    const device = this.sessUserDevices[deviceId];
    const existing = device?.Config?.LoRaWAN?.Points ?? [];
    let points: LoRaWANDevicePoint[];

    if (oldPoint) {
      const oldId = JSON.stringify(oldPoint.ID);
      points = existing.map(p => JSON.stringify(p.ID) === oldId ? point : p);
    } else {
      points = [...existing, point];
    }

    return this.patchDevice$(deviceId, {
      Config: { ...device.Config, LoRaWAN: { ...device.Config.LoRaWAN!, Points: points } },
    });
  }


  deleteLoRaWANPoint$(point: LoRaWANDevicePoint, deviceId: DeviceId): Observable<Device> {
    const device = this.sessUserDevices[deviceId];
    const pointId = JSON.stringify(point.ID);
    const points = (device?.Config?.LoRaWAN?.Points ?? []).filter(
      p => JSON.stringify(p.ID) !== pointId,
    );

    return this.patchDevice$(deviceId, {
      Config: { ...device.Config, LoRaWAN: { ...device.Config.LoRaWAN!, Points: points } },
    });
  }


  readPointValue$(pointId: string, deviceId: DeviceId): Observable<PointValue[]> {
    return this.http.get<PointValue[]>(`${TUNNEL_BASE_URL}/remote/point-values`, {
      params: {
        MotionDeviceID: '' + deviceId,
        PointIDs: JSON.stringify([pointId]),
      },
    });
  }


  writePointValue$(pointValues: PointValue[]): Observable<null> {
    return this.http.put<null>(`${TUNNEL_BASE_URL}/remote/point-values`, pointValues);
  }
}
